import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { offeringTagAPI, OfferingTagListParams } from 'api/temple'
import { apiStatus } from 'shared/constants/AppEnum'
import { AppState } from 'store/reduxStore'
import { IApiState } from 'store/types'
import { IOfferingTag } from 'types/store/temple'
import { IOfferingTagResponse, ITagResponse, TempleIdType } from 'types/api/temple'
import { selectCurrentTempleId } from './templeSlice'

type OfferingTagListPayload = TempleIdType & OfferingTagListParams

const offeringTagAdapter = createEntityAdapter<IOfferingTagResponse>({
    sortComparer: (a, b) => (a.tagName ?? a.name ?? '').localeCompare(b.tagName ?? b.name ?? ''),
})

type InitialStateType = {
    api: IApiState,
    availableTags: ITagResponse[]
}

const initialState: InitialStateType = {
    api: {
        status: apiStatus.IDLE,
        error: null
    },
    availableTags: []
}

const normalizeList = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload
    if (payload?.results && Array.isArray(payload.results)) return payload.results
    return []
}

export const offeringTagListThunk = createAsyncThunk(
    'offeringTag/list',
    async (payload: OfferingTagListPayload = {}, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await offeringTagAPI.list({
                templeId,
                offering: payload.offering,
                tag: payload.tag,
                name: payload.name,
                o: payload.o,
            })
            return normalizeList(response.data).map(tag => {
                tag.templeId = templeId
                return tag
            })
        } catch (err: any) {
            return rejectWithValue(err.response?.data ?? err.message)
        }
    }
)

export const tagListThunk = createAsyncThunk(
    'offeringTag/tagList',
    async (payload: { tag_type?: string, templeId?: string | number } = { tag_type: '' }, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await offeringTagAPI.listTags({ ...payload, templeId })
            return normalizeList(response.data)
        } catch (err: any) {
            return rejectWithValue(err.response?.data ?? err.message)
        }
    }
)

export const offeringTagCreateThunk = createAsyncThunk(
    'offeringTag/create',
    async (payload: IOfferingTag & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await offeringTagAPI.post(payload, templeId)
            response.data.templeId = templeId
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data ?? err.message)
        }
    }
)

export const offeringTagUpdateThunk = createAsyncThunk(
    'offeringTag/update',
    async (payload: PayloadWithUrl<Partial<IOfferingTag>>, { rejectWithValue }) => {
        try {
            const response = await offeringTagAPI.update(payload.url, payload.data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data ?? err.message)
        }
    }
)

export const offeringTagRetrieveThunk = createAsyncThunk(
    'offeringTag/retrieve',
    async (payload: (IdOrUrlType & TempleIdType), { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            let params
            if ('id' in payload && payload.id) params = { itemId: payload.id }
            else if ('url' in payload) params = { url: payload.url }
            else return rejectWithValue({ detail: 'Identifier not provided' })

            const response = await offeringTagAPI.get(params, templeId)
            response.data.templeId = templeId
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data ?? err.message)
        }
    }
)

export const offeringTagDeleteThunk = createAsyncThunk(
    'offeringTag/delete',
    async (payload: IdAndUrlType) => {
        await offeringTagAPI.delete(payload.url)
        return payload.id
    }
)

const offeringTagSlice = createSlice({
    name: 'offeringTag',
    initialState: offeringTagAdapter.getInitialState(initialState),
    reducers: {
        resetState(state) {
            state.api = {
                status: apiStatus.IDLE,
                error: null
            }
            state.availableTags = []
            offeringTagAdapter.removeAll(state)
        }
    },
    extraReducers: builder => {
        builder.addCase(offeringTagCreateThunk.pending, (state) => {
            state.api = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(offeringTagCreateThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            offeringTagAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(offeringTagCreateThunk.rejected, (state, action) => {
            state.api = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(offeringTagUpdateThunk.pending, (state) => {
            state.api = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(offeringTagUpdateThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            offeringTagAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(offeringTagUpdateThunk.rejected, (state, action) => {
            state.api = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(offeringTagListThunk.pending, (state) => {
            state.api = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(offeringTagListThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            offeringTagAdapter.upsertMany(state, action.payload)
        })
        builder.addCase(offeringTagListThunk.rejected, (state, action) => {
            state.api = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(tagListThunk.fulfilled, (state, action) => {
            state.availableTags = action.payload
        })

        builder.addCase(offeringTagRetrieveThunk.pending, (state) => {
            state.api = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(offeringTagRetrieveThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            offeringTagAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(offeringTagRetrieveThunk.rejected, (state, action) => {
            state.api = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(offeringTagDeleteThunk.pending, (state) => {
            state.api = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(offeringTagDeleteThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            offeringTagAdapter.removeOne(state, action.payload)
        })
        builder.addCase(offeringTagDeleteThunk.rejected, (state, action) => {
            state.api = { status: apiStatus.FAILED, error: action.error.message }
        })
    }
})

export default offeringTagSlice.reducer

export const { resetState } = offeringTagSlice.actions

export const {
    selectAll: selectAllOfferingTags,
    selectById: selectOfferingTagById,
    selectIds: selectOfferingTagIds,
} = offeringTagAdapter.getSelectors((state: AppState) => state.offeringTag)

export const selectOfferingTagStatus = (state: AppState): IApiState => state.offeringTag.api

export const selectAvailableTags = (state: AppState): ITagResponse[] => state.offeringTag.availableTags

export const selectCurrentTempleOfferingTags = createSelector(
    [selectAllOfferingTags, selectCurrentTempleId],
    (offeringTags, currentTemple) =>
        offeringTags.filter(offeringTag => offeringTag.templeId === currentTemple)
)

export const selectOfferingTagsByTempleId = createSelector(
    (state: AppState) => state,
    (_: any, templeId: string | number) => templeId,
    (state, templeId) => {
        if (!templeId) return
        return selectAllOfferingTags(state).filter(offeringTag => offeringTag.templeId === templeId)
    }
)
